import { useEffect, useMemo } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { PATH_KEYS } from "@/services/appServices";
import { useItemService } from "@/hooks/useServices";
import { useIndexingOperationsStore } from "@/stores/indexing-operations.store";
import { logger } from "@/lib/logger";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import {
  claimIndexingOperation,
  hasHandledIndexingOperation,
} from "@/lib/indexingRunRegistry";
import { restError } from "@/models/client/RestResponse";

const POLL_INTERVAL_MS = 2500;

/** What the poller needs to know about a listed document. */
export interface IndexingRunDocument {
  id: string;
  workspaceId: string;
  /** The reader may finalise a completed run (owner or write access). */
  canReindex: boolean;
}

interface UseDocumentIndexingRunsOptions {
  /** False when another poller on the same surface already owns these runs (the files tab). */
  enabled?: boolean;
}

/**
 * Tracks kildeklargøring runs started from a search surface — the Workspaces page content
 * search and global search (LAR-235) — the counterpart of the files tab's
 * useDocumentIndexingStatuses. Only runs recorded in the indexing-operations store are
 * watched; the surfaces never scan for unindexed documents. Once a run reports
 * "succeeded" it is finalised the same way the files tab does: one more reindex call
 * carrying the operation id, which lands on the API's instant heal path. Completion
 * invalidates the workspace and search queries, so every list re-reads the rebuilt index.
 *
 * Returns the ids of documents whose row should show the running spinner.
 */
export function useDocumentIndexingRuns(
  documents: IndexingRunDocument[],
  { enabled = true }: UseDocumentIndexingRunsOptions = {}
): { indexingDocumentIds: Set<string> } {
  const workspaceService = useItemService((services) => services.workspace);
  const queryClient = useQueryClient();
  const startedOperations = useIndexingOperationsStore(
    (state) => state.operations
  );
  const clearOperation = useIndexingOperationsStore((state) => state.clear);

  // Only documents on screen with a run started in this session are polled.
  const watched = useMemo(
    () =>
      enabled
        ? documents.filter((document) => document.id in startedOperations)
        : [],
    [documents, enabled, startedOperations]
  );

  const statusQueries = useQueries({
    queries: watched.map((document) => ({
      queryKey: [
        PATH_KEYS.workspace,
        "documentIndexStatus",
        document.workspaceId,
        document.id,
      ],
      queryFn: async () => {
        const response = await workspaceService.getDocumentIndexStatus(
          document.workspaceId,
          document.id
        );
        if (response.success) return response.value;
        throw restError(response);
      },
      refetchInterval: (query: { state: { data?: { state?: string } } }) =>
        query.state.data?.state === "running" ? POLL_INTERVAL_MS : false,
    })),
  });

  const finalise = useMutation({
    mutationKey: [PATH_KEYS.workspace, "finaliseSearchSurfaceReindex"],
    mutationFn: async (params: {
      workspaceId: string;
      documentId: string;
      operationId: string;
    }) => {
      const response = await workspaceService.reindexDocument(
        params.workspaceId,
        params.documentId,
        params.operationId
      );
      if (response.success) return response.value;
      throw restError(response);
    },
  });

  const statusSignature = statusQueries
    .map((q) => `${q.data?.state ?? ""}:${q.data?.operationId ?? ""}`)
    .join("|");

  useEffect(() => {
    watched.forEach((document, index) => {
      const status = statusQueries[index]?.data;
      if (!status?.operationId) return;
      if (hasHandledIndexingOperation(status.operationId)) return;
      const operationId = status.operationId;

      if (status.state === "succeeded" && document.canReindex) {
        if (!claimIndexingOperation(operationId)) return;
        // mutateAsync, not mutate: per-call callbacks of mutate only fire for the latest
        // call, so a batch of runs finishing together would lose all but the last.
        void finalise
          .mutateAsync({
            workspaceId: document.workspaceId,
            documentId: document.id,
            operationId,
          })
          .then(() => {
            clearOperation(document.id);
            queryClient.invalidateQueries({
              queryKey: [PATH_KEYS.workspace],
              exact: false,
            });
            queryClient.invalidateQueries({
              queryKey: [PATH_KEYS.search, "tenant"],
              exact: false,
            });
            showSuccessToast("Kildeklargøring er fuldført.");
          })
          .catch((error: unknown) => {
            clearOperation(document.id);
            logger.error("Failed to finalise a completed indexing run", {
              operation: "searchSurfaceFinaliseReindex",
              error,
              workspaceId: document.workspaceId,
              documentId: document.id,
              operationId,
            });
            showErrorToast(
              "Kildeklargøringen blev fuldført, men status kunne ikke opdateres. Genkør kildeklargøring."
            );
          });
      } else if (status.state === "failed") {
        if (!claimIndexingOperation(operationId)) return;
        clearOperation(document.id);
        logger.error("Background indexing run failed", {
          operation: "searchSurfaceIndexStatus",
          error: new Error(status.error ?? "unknown"),
          workspaceId: document.workspaceId,
          documentId: document.id,
          operationId,
        });
        showErrorToast(
          "Kildeklargøring fejlede. Genkør kildeklargøring for at prøve igen."
        );
      }
    });
    // The signature keys this effect to real status changes; statusQueries is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched, statusSignature]);

  const indexingDocumentIds = useMemo(() => {
    const ids = new Set<string>();
    watched.forEach((document, index) => {
      const state = statusQueries[index]?.data?.state;
      // Running, or accepted this session but not yet reported: spin. "succeeded" spins
      // until the finalisation above has cleared it from the store.
      if (state === "running" || state === "succeeded" || state === undefined) {
        ids.add(document.id);
      }
    });
    return ids;
  }, [watched, statusQueries]);

  return { indexingDocumentIds };
}

export default useDocumentIndexingRuns;
